import React, { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import LoadingButton from "../../components/button/LoadingButton/LoadingButton"
import Loading from "../../components/general/Loading"
import ReturnPageButton from "../../components/general/ReturnButton"
import { getRequester } from "../../functions/FilterUsers"
import AuthenticatedLayout from "../../layout/AuthenticatedLayout"
import * as FeedbackService from "../../services/FeedbackService"
import { useAppSelector } from "../../stores/hooks"
import { RootState } from "../../stores/store"
import "./FeedbackInvitation.scss"
import { t } from "i18next"
import { formatDateToView } from "../../functions/DateTime"


const FeedbackInvitation = (): React.JSX.Element => {
  const { id, invitation_code } = useParams()
  const [loading, setLoading] = useState<boolean>(true)
  const [acceptSuccess, setAcceptSuccess] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [requester, setRequester] = useState<IUser>({} as IUser)
  const [feedbackCanvas, setFeedbackCanvasData] = useState<IFeedback>({} as IFeedback)
  const [invitatedUser, setInvitatedUser] = useState<IUser>({} as IUser)
  const session = useAppSelector((state: RootState) => state.session)
  const [acceptStatus, setAcceptStatus] = useState<"idle" | "submitting" | "submitted">("idle")
  const [rejectStatus, setRejectStatus] = useState<"idle" | "submitting" | "submitted">("idle")

  useEffect(() => {
    loadFeedback()
  }, [])

  const navigate = useNavigate()

  const loadFeedback = useCallback(async () => {
    const getError = (data: any): void => {
      setLoading(false)
    }

    const getSuccess = (data: any): void => {
      setLoadedData(data)
      setLoading(false)
    }

    if (id) {
      await FeedbackService.get(id, getSuccess, getError)
    } else {
      setLoading(false)
    }
  }, [id])

  const setLoadedData = (data: IFeedback): void => {
    setFeedbackCanvasData(data)
    setRequester(getRequester(data.users) || {} as IUser)
    data.users.forEach((user: IUser) => {
      if (user.invitation?.code === invitation_code) {
        setInvitatedUser(user)
      }
    })
  }

  const emailMismatch = Boolean(
    (session?.email || '').trim() &&
    (invitatedUser?.email || '').trim() &&
    session.email.trim().toLowerCase() !== invitatedUser.email.trim().toLowerCase()
  )

  if (!loading && emailMismatch) {
    return (
      <AuthenticatedLayout>
        <div className="d-flex justify-content-center align-self-center">
          <div className="d-flex flex-column form-container justify-content-center align-items-center gap-4">
            <h1 className="mb-3">{t("feedbackInvitation.title")}</h1>
            <div className="alert alert-warning w-100 text-center">
              {`Esse convite não é para o email ${session.email}`}
            </div>
            <ReturnPageButton textButton={t("feedback.actions.cancel")} redirectTo="/feedbacks" />
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  const acceptInvitation = async () => {
    setAcceptStatus("submitting")

    await FeedbackService.acceptInvitation(feedbackCanvas.id, invitatedUser.invitation.code, {
      id: session.id,
      name: session.name,
      email: session.email,
      photo_url: session.picture,
      invitation_status: 'accepted',
    } as IAcceptInvitationPayload, () => {
      setLoading(false)
      setAcceptSuccess(true)
      setAcceptStatus("submitted")
      setTimeout(() => {
        navigate('/feedbacks')
      }, 1000)

    }, (error: any) => {
      setAcceptStatus("idle")
      setLoading(false)
      setErrorMessage(error)
    })
  }

  const rejectInvitation = async () => {
    setRejectStatus("submitting")
    await FeedbackService.rejectInvitation(feedbackCanvas.id, invitatedUser.invitation.code, () => {
      setRejectStatus("submitted")
      navigate('/feedbacks')
    }, (error: any) => {
      setRejectStatus("idle")
      setErrorMessage(error)
    })
  }

  const renderErrorMessage = (): React.JSX.Element => {
    if (!errorMessage) return <></>

    return <div className="alert alert-warning">{errorMessage}</div>
  }

  const renderSuccessMessage = (): React.JSX.Element => {
    if (!acceptSuccess) return <></>

    return <div className="alert alert-success">Convite aceito com sucesso</div>
  }

  const renderRoleMessage = (): React.JSX.Element => {
    if (invitatedUser.role === 'participant') {
      return (
        <div className="d-flex flex-column gap-4">
          <h3 className="fs-6"><strong> {t("feedbackInvitation.textInvitation.initial")} {requester.name} {t("feedbackInvitation.textInvitation.participant")} {formatDateToView(feedbackCanvas.schedule_date)} {t("feedbackDate.preposition")} {feedbackCanvas.schedule_time}</strong></h3>
          <p className="fs-6">{t("feedbackInvitation.textInvitation.participantText")}</p>
        </div>
      )
    } else if (invitatedUser.role === 'facilitator') {
      return (
        <>
          <h3 className="fs-6"><strong> {t("feedbackInvitation.textInvitation.initial")} {requester.name} {t("feedbackInvitation.textInvitation.facilitator")} {formatDateToView(feedbackCanvas.schedule_date)} {t("feedbackDate.preposition")} {feedbackCanvas.schedule_time}</strong></h3>
          <p className="fs-6">{t("feedbackInvitation.textInvitation.facilitatorText")}</p>
        </>
      )
    } else {
      return (
        <div className="alert alert-warning">Não foi possível carregar o convite</div>
      )
    }
  }

  if (loading) return (
    <AuthenticatedLayout>
      <div id="feedback-list" className="d-flex flex-column align-items-center justify-content-center">
        <Loading title="Carregando dados do Feedback Canvas..." />
      </div>
    </AuthenticatedLayout>
  )

  return (
    <AuthenticatedLayout>
      <div className="d-flex justify-content-center align-self-center">
        <div className="d-flex flex-column form-container justify-content-center align-items-center gap-4">
          <h1 className="mb-5">{t("feedbackInvitation.title")}</h1>

          {renderRoleMessage()}
          {renderErrorMessage()}
          {renderSuccessMessage()}

          <div className="d-flex flex-row form-container justify-content-between align-items-center gap-4 w-100 button-container">
            <LoadingButton
              onClick={acceptInvitation}
              type="submit"
              className="btn btn-primary"
              status={acceptStatus}
              idleText={t("feedbackInvitation.actions.acceptInvitation")}
              submittingText="Aceitando..."
              submittedText="Convite aceito"
            />

            <div className="d-flex flex-row gap-4">

              <LoadingButton
                onClick={rejectInvitation}
                type="submit"
                className="btn btn-danger"
                status={rejectStatus}
                idleText={t("feedbackInvitation.actions.rejectInvitation")}
                submittingText="Rejeitando..."
                submittedText="Convite rejeitado"
              />
              <ReturnPageButton textButton={t("feedbackInvitation.actions.cancel")} redirectTo="/feedbacks" />
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

export default FeedbackInvitation
